import React from 'react';
import classnames from 'classnames';
import { Icon, IconProps } from '../icon';
import { CommonInputProps } from '../types';
import { InputMessage } from '../input-message';

import styles from './text-input.module.scss';

const baseClass = 'text-input';
const iconClass = styles[`${baseClass}__icon`];

export type TextInputProps = {
  icon?: IconProps['name'];
  value: React.InputHTMLAttributes<HTMLInputElement>['value'];
} & CommonInputProps &
  Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    | 'type'
    | 'minLength'
    | 'maxLength'
    | 'onClick'
    | 'onFocus'
    | 'onBlur'
    | 'min'
    | 'max'
    | 'autoComplete'
    | 'onChange'
    | 'onKeyDown'
    | 'autoFocus'
  >;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      icon,
      type = 'text',
      id,
      label,
      disabled,
      name,
      onChange,
      message,
      className,
      value,
      required,
      onFocus,
      onBlur,
      onClick,
      minLength,
      maxLength,
      min,
      max,
      autoComplete,
      isInvalid,
      onKeyDown,
      autoFocus
    },
    ref
  ) => {
    const classes = classnames(
      styles[baseClass],
      {
        [styles[`${baseClass}--error`]]: isInvalid
      },
      className
    );
    const labelId = `${id}__label`;
    const messageId = `${id}__message`;

    const chooseIcon = () => {
      if (type === 'date') {
        return <Icon name="calendar--small" className={iconClass} />;
      }

      if (type === 'time') {
        return <Icon name="duration--small" className={iconClass} />;
      }

      if (icon) {
        return <Icon name={icon} className={iconClass} />;
      }
    };

    return (
      <div className={classes} data-testid="input-component">
        <input
          className={styles[`${baseClass}__element`]}
          id={id}
          type={type}
          disabled={disabled}
          name={name}
          value={value}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          onChange={onChange}
          onFocus={onFocus}
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onClick={onClick}
          aria-label={label}
          placeholder=" " // empty space as placeholder enables css selector :placholder-shown
          ref={ref}
          min={min}
          max={max}
          aria-labelledby={labelId}
          aria-invalid={Boolean(isInvalid)}
          aria-describedby={messageId}
          autoComplete={type === 'password' ? undefined : autoComplete}
        />
        <label htmlFor={id} className={styles[`${baseClass}__label`]} id={labelId}>
          {label}
        </label>
        {!disabled && chooseIcon()}
        {message && (
          <InputMessage
            isError={isInvalid}
            message={message}
            disabled={disabled}
            className={styles[`${baseClass}__input-message`]}
            id={messageId}
          />
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
